/**
 * legal-page — the public /legal page: terms, privacy and the imprint, each
 * its own anchor-addressable section (#terms, #privacy, #imprint) at one
 * permanent, linkable URL — § 5 DDG requires the provider details "leicht
 * erkennbar und unmittelbar erreichbar", which an overlay behind a checkout
 * link never was. Terms and privacy resolve through `resolveLegalDocument`
 * (@momo/i18n) — the operator's own published override once fully
 * published, else the platform default — and render through the existing
 * `<legal-document>` for the document body, embedded (this page owns the one
 * scroll region; the component's own bounded card belongs to an overlay).
 * The imprint renders
 * through `<legal-imprint>` (embedded, non-dismissible), assembled directly
 * from `Workspace.legal`, never stored. When that block is incomplete
 * (`imprintAvailable`, legal.state.ts — no block at all, or
 * one with no contact channel) the imprint section is absent from the DOM
 * entirely, not a table of "nicht verfügbar" — a withheld statutory
 * disclosure beats a fabricated one, per `Workspace.legal`'s own rule.
 *
 * Reachable cold: `#<section>` in the URL scrolls straight to that section
 * on mount (a fresh tab landing on a shared `/legal#imprint` link), and
 * legal-footer.view.ts's own links set the same hash after navigating here.
 */
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { html } from "lit";
import { when } from "lit/directives/when.js";
import { filter, take, takeUntil } from "rxjs";
import "../analytics/analytics-opt-out.view.ts";
import { LocalizeMixin } from "../i18n/localize.ts";
import "../storefront/brand-bar.view.ts";
import { workspaceDomain } from "../workspace/workspace.state.ts";
import "./legal-document.view.ts";
import "./legal-imprint.view.ts";
import {
	imprintAvailable,
	imprintIdentity,
	legalContentState,
	legalDocumentFor,
} from "./legal.state.ts";

@schmancyElement("legal-page")
export class LegalPage extends LocalizeMixin(SchmancyElement) {
	override connectedCallback() {
		super.connectedCallback();
		// Once the documents stand: before then the sections above the anchor
		// are skeletons, and a scroll taken against them lands short.
		legalContentState.$.pipe(
			filter((legal) => legal.settled),
			take(1),
			takeUntil(this.disconnecting),
		).subscribe(() => this.scrollToHash());
	}

	private scrollToHash() {
		const id = location.hash.slice(1);
		if (!id) return;
		requestAnimationFrame(() =>
			this.renderRoot
				.querySelector(`#${CSS.escape(id)}`)
				?.scrollIntoView({ behavior: "smooth", block: "start" }),
		);
	}

	override render() {
		const locale = this.uiLanguage;
		const unavailable = this.t("legal.identityUnavailable");
		const {
			document: termsDoc,
			identityUnavailable: termsIdentityUnavailable,
		} = legalDocumentFor("terms", locale, workspaceDomain.get(), unavailable);
		const {
			document: privacyDoc,
			identityUnavailable: privacyIdentityUnavailable,
		} = legalDocumentFor("privacy", locale, workspaceDomain.get(), unavailable);

		return html`
			<div ${overflowWithin()} class="h-full">
				<div class="mx-auto flex max-w-3xl flex-col gap-8 px-4 pb-16 pt-eye sm:px-6">
					<brand-bar></brand-bar>

					${when(
						imprintAvailable.get(),
						() => html`
							<div id="imprint" class="rounded-2xl bg-surface-containerLow">
								<legal-imprint
									.legal=${imprintIdentity.get()}
									.dismissible=${false}
								></legal-imprint>
							</div>
						`,
					)}

					<!-- .embedded: this page owns the one scroll region. Left to its
					     own overlay geometry each document would cap at 80vh and
					     scroll internally, and that inner scroller's
					     \`overscroll-behavior: contain\` would stop a reader's wheel
					     dead — privacy, and the imprint above it, unreachable while
					     the cursor sits over terms. On the page § 5 DDG requires to
					     be immediately reachable, that is the whole point missed. -->
					${when(
						!legalContentState.value.settled,
						() => html`
							<schmancy-visually-hidden role="status" aria-live="polite">${this.t("legal.loading")}</schmancy-visually-hidden>
						`,
					)}
					<!-- The section anchors stand from the first render, so a shared
					     /legal#privacy link has its target while the text is still
					     on the way. -->
					<div id="terms">
						${when(
							legalContentState.value.settled,
							() => html`
								<legal-document
									.document=${termsDoc}
									?identityUnavailable=${termsIdentityUnavailable}
									.dismissible=${false}
									.embedded=${true}
								></legal-document>
							`,
							() => html`
								<div aria-hidden="true" class="flex flex-col gap-3 rounded-2xl bg-surface-containerLow p-6">
									<schmancy-skeleton shape="text" class="w-1/3"></schmancy-skeleton>
									<schmancy-skeleton class="h-40 w-full rounded-xl"></schmancy-skeleton>
								</div>
							`,
						)}
					</div>

					<!-- The opt-out switch is slotted into the privacy document rather
					     than placed under it: §3 states that the switch at the end of
					     that clause stops the measurement, and a control sitting
					     anywhere else makes the sentence a lie. The document places it
					     by the clause's own heading (appendAfterHeading) and knows
					     nothing about what it is. -->
					<div id="privacy">
						${when(
							legalContentState.value.settled,
							() => html`
								<legal-document
									.document=${privacyDoc}
									?identityUnavailable=${privacyIdentityUnavailable}
									.dismissible=${false}
									.embedded=${true}
									.appendAfterHeading=${this.t("legal.privacy.s3.heading")}
								>
									<analytics-opt-out></analytics-opt-out>
								</legal-document>
							`,
							() => html`
								<div aria-hidden="true" class="flex flex-col gap-3 rounded-2xl bg-surface-containerLow p-6">
									<schmancy-skeleton shape="text" class="w-1/3"></schmancy-skeleton>
									<schmancy-skeleton class="h-40 w-full rounded-xl"></schmancy-skeleton>
								</div>
							`,
						)}
					</div>
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"legal-page": LegalPage;
	}
}
